/*
 *  			'License for ONE AXA SOFTWARE - Inner Source'
 * 					L O A S - I S
 * 
 * The following License aims to be applied between the AXA Companies, according to the following definition:
 * "AXA Companies" means AXA, Société Anonyme with a “AXA Companies ” Board of Directors having its principal offices at 25, avenue Matignon, 75008 Paris, registered on the Commercial Registry of Paris under the number 572 093 920; and (i) any other company controlled by, or controlling AXA, with a company being considered as controlling another: (a) when it holds directly or indirectly a portion of the capital according to it the majority of the voting rights in general meetings of shareholders of this company; (b) when it holds solely the majority of the voting rights in this company by virtue of an agreement concluded with other partners or shareholders and which is not contrary to the interest of the company; (c) when it determines de facto, by voting rights which it holds, the decisions in the general meetings of shareholders of this company; (d) in any event, when it holds, directly or indirectly, a portion of voting rights greater than 40% and when no other partner or shareholder holds directly or indirectly a portion which is greater than its own; (ii) any economic interest group in which AXA and/or one or more other Companies of the AXA Group participates for at least 50% in operating costs; (iii) in the cases where the law applicable to a company limits voting rights or control (such as defined here in above), this company will be deemed to be a company of the AXA Group, if the voting rights in 2 Binding Corporate Rules – AXA Group – version 2013 general shareholders’ meetings or the control held by a Company of the AXA Group reaches the maximum amount fixed by said applicable law; and (iv) all AXA Companies constitute the “AXA Group” (hereafter "One AXA").  (Binding Corporate Rules - AXA Group - version 2019).
 * 
 * Software credits: 
 * 	Creator: AXA GO GETD
 * 	Main contributors: AXA GO GETD
 * 
 * Permission is hereby granted, free of charge, to any person or entity within One AXA obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software within One AXA, including the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
 * 
 * The above credit notice and this permission notice shall be included in all copies or substantial portions of the Software.
 * Any improvement or modification of the Software shall be made available to the whole One AXA, under the terms of this License.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

/**
 * ### KPI Cards
 * ![header](../images/frontend/dashboard/KPICards.png)
 *@function
 * @memberOf DashboardPage
 * @name kpiCards
 * @param {query} Object - Timeframe (fromDate & toDate) where we are looking KPI's info
 * @example 
 * <kpiCards
 *  query ={ 
      fromDate: "2022-10-02T22:00:00.000Z",
      toDate: "2022-11-02T22:59:59.999Z"
    }
  }/>
}
 */

import React from 'react';

import { isEmpty } from '@utils/validation/isEmpty';
import { MESSAGES } from '@translations/labels';
import DSCard from '@design-system/card/DSCard';
import SvgIconUser from '@svg/IconUserCircle';
import SvgIconConversation from '@svg/IconConversationCircle';
import { InfoTooltip } from '@components/commons/tooltip/infoTooltip';
import { ACCENT, COLORS, ANALYTICS_CASE } from '@constants';
import useKpiCards from './useKpiCards';
import style from './kpiCardsStyles';

const formatFloat = rawNum => rawNum.toFixed(2);

const labels = MESSAGES.commons;

const KpiCards = ({ query }) => {
  const { options, loading } = useKpiCards();

  if (loading) {
    const nineCards = Array.from(Array(9).keys());
    return nineCards.map(i => {
      return (
        <DSCard key={i}>
          <div className="loader">
            <p>{labels.loading}</p>
          </div>
        </DSCard>
      );
    });
  }

  const buildAnalyticsQuery = (card, q) => {
    const analyticsQuery = { ...q };
    if (card.id) {
      analyticsQuery.isRemoteDataSource = true;
      if (card.id === ANALYTICS_CASE.MISSED_MESSAGES) {
        analyticsQuery['nlp.intent'] = labels.none;
      }
      if (card.id === ANALYTICS_CASE.UNUSED_INTENTS) {
        analyticsQuery.isCurrentRelease = true;
      }
    }
    return JSON.stringify(analyticsQuery);
  };

  const getTotal = ({ total, formatFloat: isFormatFloat, zeroValue }) => {
    if (!total) return zeroValue || total;
    if (isFormatFloat) return formatFloat(total);
    return total;
  };

  return (
    options &&
    options.map((card, i) => {
      const colorDesv = card.desv >= 0 ? ACCENT.ok : ACCENT.ko;
      return (
        <DSCard key={`${card.type}-${i}`} hasLink={!!card.link}>
          <>
            <span className={`inline-block icon ${card.icon === labels.users ? COLORS.terciary : COLORS.primary}`}>
              <div>{card.icon === labels.users ? <SvgIconUser /> : <SvgIconConversation />}</div>
            </span>
            <div className="info">
              <div className="flex row space-between">
                <p className="total">
                  <strong>{getTotal(card)}</strong>
                </p>
                {/* {!isEmpty(card.desv) && (
                  <p className={`desv ${colorDesv}`}>
                    <span className={`arrow-icon ${colorDesv === ACCENT.ko ? 'rotate-180deg' : ''}`.trim()}>
                      <SvgIconArrowVertical />
                    </span>
                    <span className="text-desv">{`${colorDesv === ACCENT.ok ? '+' : ''}${card.desv}%`}</span>
                  </p>
                )} */}
                {!isEmpty(card.tooltip) && (
                  <div className="relative">
                    <InfoTooltip label={card.tooltip} />
                  </div>
                )}
              </div>
              <div className="flex row space-between">
                <p className="kpi">
                  {card.name}
                  {card.extraName && <span style={{ fontSize: '0.8em' }}> {card.extraName}</span>}
                </p>
                {/*
                // Available in next update, when calculating "diff from previous range"
                */}
                {card.link && !!card.total && (
                  <p className="link block">
                    <a
                      href={`${card.link}&q=${buildAnalyticsQuery(card, query)}`}
                      target="_self"
                      className="link-primary"
                    >
                      {labels.linkMoreInfo}
                    </a>
                  </p>
                )}
              </div>
            </div>
          </>
          <style jsx>{style}</style>
        </DSCard>
      );
    })
  );
};

export default KpiCards;
